import { useState } from "react";

import {
    Quest,
    Objective,
    OBJECTIVE_TYPE,
    NotificationConfig,
} from "../../interfaces";
import {
    DifficultySelect,
    NotificationSettingEdit,
    ObjectiveEdit,
    ObjectiveEntry,
} from "../molecules";
import { Button } from "../atoms";

import "../../styles/questedit.css";
import "../../styles/objective.css";

const QuestEdit = ({
    quest,
    setQuest,
    submitObjective,
    submit,
    cancel,
}: {
    quest: Quest;
    setQuest: React.Dispatch<React.SetStateAction<Quest>>;
    submitObjective: (x: Objective) => void;
    submit: () => void;
    cancel?: () => void;
}) => {
    const setTitle = (val: string) => {
        setQuest((p) => ({ ...p, title: val }));
    };

    const setDesc = (val: string) => {
        setQuest((p) => ({ ...p, desc: val }));
    };

    const setDateStarted = (val: string) => {
        setQuest((p) => ({ ...p, dateStarted: val }));
    };

    const setEndDate = (val: string) => {
        setQuest((p) => ({ ...p, intendedEndDate: val }));
    };

    const setDifficulty = (val: number) => {
        setQuest((p) => ({ ...p, difficulty: val }));
    };

    const setReminderNoti = (val: NotificationConfig) => {
        setQuest((p) => ({
            ...p,
            notification: { ...p.notification, reminder: val },
        }));
    };

    const setPromptNoti = (val: NotificationConfig) => {
        setQuest((p) => ({
            ...p,
            notification: { ...p.notification, prompt: val },
        }));
    };

    const setDaysLeftNoti = (val: NotificationConfig) => {
        setQuest((p) => ({
            ...p,
            notification: { ...p.notification, daysLeft: val },
        }));
    };

    const [addingObjective, setAddingObjective] = useState(false);
    const [workingObj, setWorkingObj] = useState<Objective>({
        id: "",
        dateCreated: "",
        dateUpdated: "",
        title: "",
        type: OBJECTIVE_TYPE.FACT,
        goal: 1,
        value: 0,
    });

    const cancelObjectiveAdd = () => {
        setWorkingObj({
            id: "",
            dateCreated: "",
            dateUpdated: "",
            title: "",
            type: OBJECTIVE_TYPE.FACT,
            goal: 1,
            value: 0,
        });
        setAddingObjective(false);
    };

    return (
        <div className="qeditWrapper">
            <input
                className="title"
                type="text"
                placeholder="Title"
                value={quest.title}
                onChange={(e) => setTitle(e.target.value)}
            />

            <textarea
                className="desc"
                rows={5}
                placeholder="Description"
                value={quest.desc}
                onChange={(e) => setDesc(e.target.value)}
            />

            <div className="dateRange">
                <div className="datePickerWrapper">
                    <label htmlFor="date started">Start Date *</label>
                    <input
                        type="date"
                        name="date started"
                        value={quest.dateStarted}
                        onChange={(e) => setDateStarted(e.target.value)}
                    />
                </div>

                <div className="datePickerWrapper">
                    <label htmlFor="end date">Intended End Date</label>
                    <input
                        type="date"
                        name="end date"
                        value={quest.intendedEndDate}
                        onChange={(e) => setEndDate(e.target.value)}
                    />
                </div>
            </div>

            <div>Difficulty *</div>
            <DifficultySelect
                difficulty={quest.difficulty}
                setDifficulty={setDifficulty}
            />

            <div className="objectivesWrapper">
                {quest.objectives.length > 0 && <h2>Objectives</h2>}
                {quest.objectives.map((obj, i) => (
                    <ObjectiveEntry key={`obj-${i}`} objective={obj} />
                ))}
                {addingObjective ? (
                    <div className="addObjectiveWrapper">
                        <ObjectiveEdit
                            objective={workingObj}
                            updateObjective={setWorkingObj}
                        />
                        <div className="addObjectiveActions">
                            <Button
                                text="Submit"
                                onClick={() => {
                                    submitObjective(workingObj);
                                    cancelObjectiveAdd();
                                }}
                            />
                            <Button
                                text="Cancel"
                                onClick={cancelObjectiveAdd}
                            />
                        </div>
                    </div>
                ) : (
                    <div>
                        <Button
                            text="Add objective"
                            variant="outline"
                            onClick={() => setAddingObjective(true)}
                        />
                    </div>
                )}
            </div>

            <details className="notiSettingsWrapper">
                <summary>Notification Settings</summary>

                <div className="notiSettingsList">
                    <NotificationSettingEdit
                        name="Reminder"
                        config={quest.notification.reminder}
                        updateConfig={setReminderNoti}
                    />
                    <NotificationSettingEdit
                        name="Prompt"
                        config={quest.notification.prompt}
                        updateConfig={setPromptNoti}
                    />
                    <NotificationSettingEdit
                        name="Days Left"
                        config={quest.notification.daysLeft}
                        updateConfig={setDaysLeftNoti}
                    />
                </div>
            </details>

            <div>
                <Button
                    style={{ marginRight: "1em" }}
                    text="Submit"
                    onClick={submit}
                />
                {cancel && <Button text="Cancel" onClick={cancel} />}
            </div>
        </div>
    );
};

export default QuestEdit;
